import React from 'react';
import { CATEGORIES } from '../../contracts/taxonomy';
import { categoryIcon } from './categoryMeta';

// FACE BREAKDOWN — the selectable master list of the 7 scored categories.
// Sorted worst-first (lowest score = most to gain). Selecting a category opens
// its detail (the connected panel to the right on desktop; a modal on mobile).
//
// `bare` drops the standalone card chrome so it can sit inside the shared
// breakdown+detail widget; otherwise it renders as its own panel (mobile).

const ScoreBar = ({ score }) => (
  <div className="h-[3px] rounded-full bg-[rgba(255,255,255,0.1)] overflow-hidden">
    <div
      className="h-full rounded-full"
      style={{
        width: `${Math.max(0, Math.min(100, score || 0))}%`,
        background: 'linear-gradient(90deg, #9A7B40, #E6C083)',
      }}
    />
  </div>
);

const CategoryBreakdown = ({ categories, selectedKey, onSelect, header = 'FACE BREAKDOWN', bare = false }) => {
  const scoreByKey = {};
  for (const c of categories || []) scoreByKey[c.key] = c.score;
  const ordered = [...CATEGORIES].sort(
    (a, b) => (scoreByKey[a.key] ?? 999) - (scoreByKey[b.key] ?? 999),
  );

  return (
    <div
      className={[
        'flex flex-col h-full min-h-0',
        bare ? '' : 'bg-[rgba(32,32,31,0.72)] border border-hairline-soft rounded-panel',
      ].join(' ')}
    >
      <div className="flex items-center justify-between px-5 pt-5 pb-2.5 border-b border-hairline-soft shrink-0">
        <span className="font-display uppercase tracking-[0.24em] text-[#E8E2D6]" style={{ fontSize: 12 }}>
          {header}
        </span>
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="w-1.5 h-1.5 rounded-full bg-aurum" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#9A7B40]" />
          <span className="w-1.5 h-1.5 rounded-full bg-vein-dim" />
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-3 py-2">
        {ordered.map((cat) => {
          const Icon = categoryIcon(cat.key);
          const score = scoreByKey[cat.key];
          const selected = cat.key === selectedKey;
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => onSelect && onSelect(cat.key)}
              aria-pressed={selected}
              aria-label={`${cat.label} ${score != null ? score : 'not scored'}`}
              className={[
                'w-full text-left flex items-center gap-3.5 px-2.5 py-3 my-0.5 rounded-field cursor-pointer transition-colors duration-200',
                selected
                  ? 'bg-[rgba(201,164,92,0.1)] border border-[rgba(201,164,92,0.45)]'
                  : 'bg-transparent border border-transparent hover:bg-[rgba(255,255,255,0.03)]',
              ].join(' ')}
            >
              <span className="text-aurum shrink-0" aria-hidden="true">
                <Icon size={18} strokeWidth={1.5} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-display text-[#E8E2D6]" style={{ fontSize: 15 }}>
                  {cat.label}
                </span>
                <span className="block mt-1.5">
                  <ScoreBar score={score} />
                </span>
              </span>
              <span className="font-display font-medium text-aurum tabular-nums shrink-0" style={{ fontSize: 22 }}>
                {score != null ? score : '—'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryBreakdown;
